import { Component, ComponentFactoryResolver, ViewEncapsulation, ElementRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService, ModalSize, SelectListCellRenderer, Secure } from '@cv-invision/core';
import { ICellRendererParams } from 'ag-grid-community';
import { URLS, LOGS } from '../../model/nexsys-monitor.constant';
import { OrdersLogComponent } from '../orders-log/orders-log.component';
import { DROPDOWN_BUTTON_ITEM_TYPE, DropdownButtonItem } from '@cv-invision/cv.component-library.ux';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PERMISSION } from '../../../model/global-configuration.constant';

@Component({
	selector: 'cv-custom-orders-action-grid-cell',
	templateUrl: './custom-action-grid-cell.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./custom-action-grid-cell.component.scss']
})
export class CustomActionNexsysOrdersCellComponent extends SelectListCellRenderer implements OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	public params: ICellRendererParams;
	public cellValue: any;
	isValuTrust: boolean;
	isAssociated: boolean;
	isAssociatedAddendum: boolean;
	logDropdownItems: Array<DropdownButtonItem>;

	constructor(
		private router: Router,
		private componentFactoryResolver: ComponentFactoryResolver,
		private modalService: ModalService,
		public elementRef: ElementRef
	) {
		super(elementRef);
	}

	/**
	 * When the component renders, it got the possible value
	 * @param params
	 */
	agInit(params: ICellRendererParams): void {
		this.params = params;
		this.cellValue = params.data;
		this.isValuTrust = this.cellValue.provider === 'valutrust';
		this.isAssociated = !!this.cellValue.associatedProviderId;
		this.isAssociatedAddendum = !!this.cellValue.associatedAddendumProviderId;

		this.buildLogDropDownOptions();
	}

	/**
	 * Build Drop Down Options
	 */
	buildLogDropDownOptions(): void {
		this.logDropdownItems = [
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.ORDER,
				icon: 'fa-file-text-o'
			}
		];

		if (this.cellValue.provider === 'nexsys') {
			this.addNexsysItem();
		}

		if (this.cellValue.provider === 'valutrust') {
			this.addValutrustItem();
		}

		if (this.cellValue.provider === 'xpanse') {
			this.addXpanseItem();
		}

		if (this.cellValue.provider === 'fnc') {
			this.addFNCItem();
		}

		if (this.cellValue.provider === 'evo') {
			this.addEVOItem();
		}

		if (this.cellValue.origin === 'ClassOrderEntry') {
			this.addOrderExternalItem();
		}

		if (this.cellValue.provider === 'valuelink') {
			this.addValuelinkItem();
		}

		if (this.cellValue.provider === 'connexions') {
			this.addConnexionsItem();
		}

		if (this.cellValue.provider === 'lendingqb') {
			this.addLendingQBItem();
		}

		if (this.cellValue.addendumInfo) {
			this.logDropdownItems.push(
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
				},
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: LOGS.ADDENDUM,
					icon: 'fa-file-text-o'
				}
			);
		}

		if (this.cellValue.associatedProviderId) {
			this.logDropdownItems.push(
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
				},
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: LOGS.ASSOCIATED_LOGS,
					icon: 'fa-file-text-o'
				}
			);
		}

		if (this.cellValue.associatedAddendumProviderId) {
			this.logDropdownItems.push(
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
				},
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: LOGS.ASSOCIATED_ADDENDUM_LOGS,
					icon: 'fa-file-text-o'
				}
			);
		}
	}

	@Secure(PERMISSION.NEXSYS)
	private addNexsysItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.NEXSYS,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.VALUTRUST)
	private addValutrustItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.VALUTRUST,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.XPANSE)
	private addXpanseItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.XPANSE,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.FNC)
	private addFNCItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.FNC,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.EVO)
	private addEVOItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.EVO,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.ORDERS_EXTERNAL)
	private addOrderExternalItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.ORDERSEXTERNAL,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.ORDERS_EXTERNAL)
	private addValuelinkItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.VALUE_LINK,
				icon: 'fa-file-text-o'
			}
		);
	}


	@Secure(PERMISSION.CONNEXIONS)
	private addConnexionsItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.CONNEXIONS,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.CONNEXIONS)
	private addLendingQBItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.LENDINGQB,
				icon: 'fa-file-text-o'
			}
		);
	}

	/**
	 * Open selected log
	 * @param $event
	 */
	onViewLog($event: { index: number; item: DropdownButtonItem }): void {
		switch ($event.item.label) {
			case LOGS.ORDER:
				this.viewOrderLogs();
				break;
			case LOGS.NEXSYS:
				this.viewNexsysLogs();
				break;
			case LOGS.VALUTRUST:
				this.viewValuTrustLogs();
				break;
			case LOGS.XPANSE:
				this.viewXpanseLogs();
				break;
			case LOGS.FNC:
				this.viewFNCLogs();
				break;
			case LOGS.EVO:
				this.viewEVOLogs();
				break;
			case LOGS.ORDERSEXTERNAL:
				this.viewOrdersExternalLogs();
				break;
			case LOGS.ADDENDUM:
				this.viewAddedumLogs('addendum');
				break;
			case LOGS.ASSOCIATED_LOGS:
				this.viewAddedumLogs('associated');
				break;
			case LOGS.ASSOCIATED_ADDENDUM_LOGS:
				this.viewAddedumLogs('associatedAdd');
				break;
			case LOGS.VALUE_LINK:
				this.viewOrdersValuelinkLogs();
				break;
			case LOGS.CONNEXIONS:
				this.viewOrdersConnexionsLogs();
				break;
			case LOGS.LENDINGQB:
				this.viewOrdersLendingQBLogs();
				break;
		}
	}

	/**
	 * open nexsys log order
	 */
	viewOrderLogs(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: OrdersLogComponent,
				data: {
					isEditing: true,
					product: this.cellValue.id
				},
				modalConfiguration: {
					title: `Order Id ${this.cellValue.id}`,
					modalSize: ModalSize.XL
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.success) {
					//
				} else {
					// it failed :(
				}
			});
	}

	/**
	 * open nexsys log order
	 */
	viewNexsysLogs(): void {
		this.router.navigate([
			`/global-configuration/nexsys-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}

	/**
	 * open valutrust log order
	 */
	viewValuTrustLogs(): void {
		this.router.navigate([
			`/global-configuration/valutrust-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}

	/**
	 * open xpanse log order
	 */
	viewXpanseLogs(): void {
		this.router.navigate([
			'/global-configuration/xpanse-monitor/order-log/',
			this.cellValue.providerTransId,
			this.cellValue.providerId
		]);
	}

	/**
	 * Open FNC log orders
	 */
	viewFNCLogs(): void {
		this.router.navigate([
			`/global-configuration/fnc-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}

	/**
	 * Open EVO log orders
	 */
	viewEVOLogs(): void {
		this.router.navigate([
			`/global-configuration/evo-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}


	/**
	 * open Order External log order
	 */
	 viewOrdersExternalLogs(): void {
		this.router.navigate([
			'/global-configuration/order-external-monitor/order-log/',
			this.cellValue.id,
		]);
	}

	/**
	 * Open valutrust log order
	 */
	viewAddedumLogs(type: string): void {
		let id;
		if (type === 'addendum') {
			id = this.cellValue.addendumInfo;
		}

		if (type === 'associated') {
			id = this.cellValue.associatedProviderId;
		}

		if (type === 'associatedAdd') {
			id = this.cellValue.associatedAddendumProviderId;
		}
		const url = this.isValuTrust ? URLS.VALUTRUST : URLS.NEXSYS;
		this.router.navigate([`/global-configuration/${url}/order-log/`, id]);
	}

	/**
	 * open Valuelink log order
	 */
	viewOrdersValuelinkLogs(): void {
		this.router.navigate([
			`/global-configuration/valuelink-monitor/order-log/`,
			this.cellValue.providerId,
		]);
	}

	/**
	 * open Connexions log order
	 */
	viewOrdersConnexionsLogs(): void {
		this.router.navigate([
			`/global-configuration/connexions-monitor/order-log/`,
			this.cellValue.id,
		]);
	}

	/**
	 * open lendingQB log order
	 */
	viewOrdersLendingQBLogs(): void {
		this.router.navigate([
			`/global-configuration/lending-qb-monitor/order-log/`,
			this.cellValue.id,
		]);
	}

	/**
	 * Put back the value
	 * @param params
	 */
	refresh(params: ICellRendererParams): boolean {
		this.cellValue = params;
		return true;
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
